/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { contactService } from "@/lib/services";
import { CheckCircle } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

export default function ContactFormSection() {
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phone: "",
		message: "",
	});
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState("");

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
		// Clear error when user starts typing
		if (error) setError("");
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		setSuccess(false);

		try {
			const response = await contactService.create(formData);

			if (response.success) {
				setSuccess(true);
				setFormData({ name: "", email: "", phone: "", message: "" });

				// Hide success message after 5 seconds
				setTimeout(() => setSuccess(false), 5000);
			} else {
				setError(response.error || "Failed to send message. Please try again.");
			}
		} catch (err: any) {
			console.error("Contact form error:", err);
			setError(
				err.response?.data?.error ||
					"Failed to send message. Please try again later."
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<section className="py-16 md:py-24 bg-white">
			<div className="container mx-auto px-4">
				<div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
					<div className="relative h-96 rounded-2xl overflow-hidden">
						<Image
							src="/assets/black/4.jpg"
							alt="Coach with young football players"
							fill
							className="object-cover"
						/>
					</div>

					{/* Right Side - Contact Form */}
					<div>
						{/* Success Message */}
						{success && (
							<div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
								<CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
								<div>
									<p className="text-green-800 font-medium">
										Message sent successfully!
									</p>
									<p className="text-green-700 text-sm mt-1">
										We&apos;ll get back to you as soon as possible.
									</p>
								</div>
							</div>
						)}

						{/* Error Message */}
						{error && (
							<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
								<p className="text-red-600 text-sm">{error}</p>
							</div>
						)}

						<form onSubmit={handleSubmit} className="space-y-5">
							<div>
								<input
									type="text"
									name="name"
									placeholder="Name"
									value={formData.name}
									onChange={handleChange}
									required
									disabled={loading}
									className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-700 placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
								/>
							</div>

							<div>
								<input
									type="email"
									name="email"
									placeholder="Email"
									value={formData.email}
									onChange={handleChange}
									required
									disabled={loading}
									className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-700 placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
								/>
							</div>

							<div>
								<input
									type="tel"
									name="phone"
									placeholder="Phone Number"
									value={formData.phone}
									onChange={handleChange}
									disabled={loading}
									className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-700 placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
								/>
							</div>

							<div>
								<textarea
									name="message"
									placeholder="Message"
									value={formData.message}
									onChange={handleChange}
									required
									rows={7}
									disabled={loading}
									className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-gray-700 placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
								/>
							</div>

							<button
								type="submit"
								disabled={loading}
								className="px-10 py-4 bg-primary hover:bg-blue-700 text-white font-bold rounded-full transition-colors shadow-lg hover:shadow-xl text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
							>
								{loading ? (
									<>
										<svg
											className="animate-spin h-5 w-5 text-white"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											/>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											/>
										</svg>
										Sending...
									</>
								) : (
									"Send Message"
								)}
							</button>
						</form>
					</div>
				</div>
			</div>
		</section>
	);
}
